import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowDown } from "lucide-react";

const HERO_IMG = "/brand/hero-boardroom.png";

const line = {
    hidden: { y: "110%" },
    show: (i = 0) => ({ y: "0%", transition: { duration: 1.05, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Hero() {
    const { scrollY } = useScroll();
    const parallaxY = useTransform(scrollY, [0, 800], [0, 140]);
    const scale = useTransform(scrollY, [0, 800], [1, 1.06]);

    return (
        <section data-testid="hero" className="relative pt-[92px] pb-16 md:pt-[112px] md:pb-24 overflow-hidden">
            {/* Faint grid lines */}
            <div aria-hidden className="absolute inset-0 pointer-events-none">
                <div className="max-w-[1440px] mx-auto h-full px-6 md:px-10 grid grid-cols-12">
                    {Array.from({ length: 12 }).map((_, i) => (
                        <div key={i} className="border-r border-line/50 first:border-l" />
                    ))}
                </div>
            </div>

            <div className="max-w-[1440px] mx-auto px-6 md:px-10 relative">
                {/* Eyebrow */}
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.05, duration: 0.6 }} className="flex items-center gap-3 mb-8">
                    <span className="w-6 h-px bg-gold" />
                    <span className="eyebrow"><span className="text-gold">●</span> Commercial technology · Metro Vancouver, BC</span>
                </motion.div>

                {/* Kinetic headline */}
                <h1 data-testid="hero-headline" className="font-display text-[52px] sm:text-[72px] md:text-[96px] lg:text-[124px] leading-[0.9] tracking-tight text-ink">
                    <span className="mask-line"><motion.span variants={line} initial="hidden" animate="show" custom={0} className="block">Commercial technology</motion.span></span>
                    <span className="mask-line"><motion.span variants={line} initial="hidden" animate="show" custom={1} className="block">that works as hard</motion.span></span>
                    <span className="mask-line"><motion.span variants={line} initial="hidden" animate="show" custom={2} className="block">
                        as <span className="italic font-normal">your business.</span>
                    </motion.span></span>
                </h1>

                {/* Under grid: sub + hero visual */}
                <div className="mt-14 md:mt-20 grid md:grid-cols-12 gap-8 md:gap-12 items-end">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.85, duration: 0.8 }}
                        className="md:col-span-5"
                    >
                        <p className="text-ink/85 text-lg md:text-xl leading-relaxed max-w-md">
                            Final Cut Technologies installs, organizes and supports the AV, Wi-Fi, displays, audio and everyday technology that Metro Vancouver businesses depend on.
                        </p>
                        <p className="mt-6 text-sm text-muted max-w-sm">
                            Serving restaurants, hotels, clinics, retail spaces, offices and commercial properties throughout the Lower Mainland.
                        </p>

                        <div className="mt-10 flex flex-wrap gap-3">
                            <Link
                                data-testid="hero-primary-cta"
                                to="/contact"
                                className="inline-flex items-center gap-2 bg-ink text-bg px-6 py-4 text-sm font-medium hover:bg-accent transition-colors duration-300"
                            >
                                Request a Free Site Assessment
                                <ArrowUpRight className="w-4 h-4" />
                            </Link>
                            <Link
                                data-testid="hero-secondary-cta"
                                to="/services"
                                className="inline-flex items-center gap-2 border border-ink/70 text-ink px-6 py-4 text-sm hover:bg-ink hover:text-bg transition-colors duration-300"
                            >
                                Explore Our Services
                            </Link>
                        </div>
                    </motion.div>

                    {/* Spotlighted hero image */}
                    <motion.div
                        initial={{ opacity: 0, y: 40, clipPath: "inset(100% 0 0 0)" }}
                        animate={{ opacity: 1, y: 0, clipPath: "inset(0% 0 0 0)" }}
                        transition={{ delay: 0.55, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                        className="md:col-span-7 relative"
                    >
                        <div className="relative aspect-[16/11] md:aspect-[16/10] overflow-hidden bg-ink">
                            <motion.img
                                src={HERO_IMG}
                                alt="Modern commercial boardroom with integrated commercial AV in a Metro Vancouver office"
                                loading="eager"
                                style={{ y: parallaxY, scale }}
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            {/* Framing edges */}
                            <div className="absolute inset-0 pointer-events-none">
                                <div className="absolute top-4 left-4 w-8 h-8 border-t border-l border-bg/80" />
                                <div className="absolute top-4 right-4 w-8 h-8 border-t border-r border-bg/80" />
                                <div className="absolute bottom-4 left-4 w-8 h-8 border-b border-l border-bg/80" />
                                <div className="absolute bottom-4 right-4 w-8 h-8 border-b border-r border-bg/80" />
                            </div>
                            {/* Ticker overlay */}
                            <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end text-bg">
                                <span className="tick opacity-90">[ Case · Office / Vancouver ]</span>
                                <span className="tick opacity-90">01 / 12</span>
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Scroll indicator */}
                <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 0.6 }}
                    className="mt-12 flex items-center gap-3 text-muted"
                >
                    <ArrowDown className="w-4 h-4 animate-bounce" />
                    <span className="tick uppercase">Scroll · What we build →</span>
                </motion.div>
            </div>
        </section>
    );
}
